// Removed worker-loader import

let _worker = null;
let _objectDetectInFlight = false;
let _objectCanvas = null;
let _objectCanvasCtx = null;
export let _lastObjectDetectionTime = 0;
let _objectTargetW = 0;
let _resolveWorkerPromise = null;

// Unlike the non-phone branch below (which waits 800ms then fires once via
// state.objectDetectedStart), this had no debounce of its own -- and
// triggerViolation's own cooldown explicitly skips priority violations
// (phone detection is one), so a phone held continuously in frame fired a
// fresh violation every 500ms (this runs on OBJECT_DETECTION_INTERVAL) with
// nothing to stop it -- up to 120/minute, spamming near-duplicate rows for
// what is really one ongoing episode.
let _lastPhoneReportTime = 0;
const PHONE_REPORT_COOLDOWN_MS = 3000;

export const externalDeviceState = {
  objectDetectedStart: null,
  detectedObjects: [],
};

export const externalDeviceGetters = {};

export const externalDeviceMutations = {
  SET_OBJECT_DETECTED_START(state, val) {
    state.objectDetectedStart = val;
  },
  SET_DETECTED_OBJECTS(state, objects) {
    state.detectedObjects = objects;
  },
};

export const externalDeviceActions = {
  async initObjectDetector() {
    if (_worker) {
      _worker.terminate();
    }

    _worker = new Worker(
      new URL("../workers/objectDetection.worker.js", import.meta.url)
    );

    return new Promise((resolve) => {
      _worker.onmessage = (e) => {
        if (e.data.type === "READY") {
          resolve();
        }
      };
      _worker.postMessage({ type: "INIT" });
    });
  },

  async detectProhibitedObjects({ state, commit, dispatch }, videoEl) {
    if (!_worker) {
      commit("SET_OBJECT_DETECTED_START", null);
      commit("SET_DETECTED_OBJECTS", []);
      return;
    }

    if (
      _objectDetectInFlight ||
      !videoEl ||
      videoEl.readyState < 2 ||
      !videoEl.videoWidth ||
      !videoEl.videoHeight
    ) {
      return;
    }

    _objectDetectInFlight = true;
    _lastObjectDetectionTime = Date.now();
    try {
      if (!_objectCanvas) {
        _objectCanvas = document.createElement("canvas");
        _objectCanvasCtx = _objectCanvas.getContext("2d", {
          willReadFrequently: true,
        });
      }

      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (!_objectTargetW) {
        _objectTargetW = 480;
      }
      const targetW = Math.min(_objectTargetW, vw);
      const targetH = Math.max(1, Math.round((vh / vw) * targetW));

      _objectCanvas.width = targetW;
      _objectCanvas.height = targetH;
      _objectCanvasCtx.drawImage(videoEl, 0, 0, targetW, targetH);

      // Extract raw ImageData
      const imageData = _objectCanvasCtx.getImageData(0, 0, targetW, targetH);

      const predictions = await new Promise((resolve) => {
        _resolveWorkerPromise = resolve;

        // Re-attach message handler for this specific run
        _worker.onmessage = (e) => {
          if (e.data.type === "RESULT") {
            if (_resolveWorkerPromise) {
              _resolveWorkerPromise(e.data.predictions);
              _resolveWorkerPromise = null;
            }
          }
        };

        // Pass imageData.data.buffer in the transfer list for zero-copy
        _worker.postMessage({ type: "DETECT", imageData }, [
          imageData.data.buffer,
        ]);
      });

      const prohibited = new Set([
        "cell phone",
        "mobile phone",
        "phone",
        "laptop",
        "tv",
        "tvmonitor",
        "monitor",
        "book",
        "earpiece", // Placeholder for when custom model is loaded
      ]);

      const minScoreByClass = {
        "cell phone": 0.12,
        "mobile phone": 0.12,
        phone: 0.12,
        laptop: 0.18,
        tv: 0.18,
        tvmonitor: 0.18,
        monitor: 0.18,
        book: 0.18,
        earpiece: 0.18,
      };

      const foundProhibited = (predictions || [])
        .filter((p) => {
          if (!prohibited.has(p.class)) return false;
          const min = minScoreByClass[p.class] ?? 0.18;
          return (p.score || 0) >= min;
        })
        .sort((a, b) => b.score - a.score);

      if (foundProhibited.length > 0) {
        const itemName = foundProhibited[0].class;
        const isPhone =
          itemName === "cell phone" ||
          itemName === "mobile phone" ||
          itemName === "phone";

        if (isPhone) {
          const now = Date.now();
          if (now - _lastPhoneReportTime >= PHONE_REPORT_COOLDOWN_MS) {
            _lastPhoneReportTime = now;
            dispatch("triggerViolation", {
              reason: `Prohibited object detected: ${itemName}`,
              isLog: false,
            });
          }
        } else {
          if (!state.objectDetectedStart) {
            commit("SET_OBJECT_DETECTED_START", Date.now());
          } else if (Date.now() - state.objectDetectedStart > 800) {
            dispatch("triggerViolation", {
              reason: `Prohibited object detected: ${itemName}`,
              isLog: itemName === "book",
            });
          }
        }
      } else {
        commit("SET_OBJECT_DETECTED_START", null);
      }

      commit(
        "SET_DETECTED_OBJECTS",
        (predictions || []).map((p) => p.class)
      );
    } catch (err) {
      if (_objectTargetW && _objectTargetW > 480) {
        _objectTargetW = Math.max(480, Math.round(_objectTargetW * 0.75));
      }
      commit("SET_OBJECT_DETECTED_START", null);
      commit("SET_DETECTED_OBJECTS", []);
    } finally {
      _objectDetectInFlight = false;
      _resolveWorkerPromise = null;
    }
  },

  cleanupExternalDevice() {
    if (_worker) {
      _worker.terminate();
      _worker = null;
    }
    _resolveWorkerPromise = null;
  },
};
